import { Stage, stageFromString } from '@eventbrite/webapp-constants';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';
import { FavIconLinks } from '@eventbrite/webapp-public-content';
import type { AppContext, AppProps } from 'next/app';
import NextApp from 'next/app';
import Head from 'next/head';
import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getLocaleFromRequest } from '@eventbrite/webapp-locales';

import { RoktInit } from '@/components/RoktInit';
import '@eventbrite/webapp-fonts/fonts.css';
import '@/styles/global.scss';
import '@/styles/organizer-profile-tokens.scss';
import { IntlProvider } from 'react-intl';
import { IncomingMessage } from 'http';

import { getDefaultDomainUrl, getDomainUrl } from '@/utils';
import { getFinalLocale } from '@/lib/localeInfo';
import { formatLocale, getLocaleMessages } from '../../i18n/locale';

type I18nPageProps = {
  locale: string;
  messages: Record<string, string>;
};

interface OrganizerProfileWebappProps extends AppProps<I18nPageProps> {
  serverUrl: string;
  stage: Stage;
}

function handleIntlError(error: unknown): void {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    (error as { code?: string }).code === 'MISSING_TRANSLATION'
  ) {
    return;
  }

  // eslint-disable-next-line no-console
  console.error(error);
}

export default function App({
  Component,
  pageProps,
  serverUrl,
  stage,
}: OrganizerProfileWebappProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { refetchOnWindowFocus: false },
        },
      }),
  );

  return (
    <>
      <Head>
        <FavIconLinks stage={stage} />
      </Head>
      <RoktInit stage={stage} />
      <QueryClientProvider client={queryClient}>
        <IntlProvider
          locale={pageProps.locale}
          messages={pageProps.messages}
          defaultLocale="en"
          onError={handleIntlError}
        >
          <Component {...pageProps} serverUrl={serverUrl} stage={stage} />
        </IntlProvider>
      </QueryClientProvider>
      <ConsentManagerScripts />
    </>
  );
}

App.getInitialProps = async (context: AppContext) => {
  const req = context.ctx.req as IncomingMessage;
  const appProps = await NextApp.getInitialProps(context);

  const serverUrl = getDomainUrl(req) || getDefaultDomainUrl();

  const stage = stageFromString(process.env.STAGE || Stage.TEST);
  const calculatedLocale = req ? getLocaleFromRequest(req) : undefined;
  const sourceLocale = getFinalLocale(calculatedLocale);
  const locale = formatLocale(sourceLocale);
  const messages = await getLocaleMessages(sourceLocale);

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      locale,
      messages,
    },
    stage,
    serverUrl,
  };
};
